import { useQuery } from '@tanstack/react-query';
import { auth } from '../config/firebase';

interface Product {
  id: string;
  name: string;
  description: string;
  active: boolean;
  metadata: {
    [key: string]: string;
  };
}

interface PriceOption {
  id: string;
  product: string;
  unit_amount: number;
  currency: string;
  interval: string;
  interval_count: number;
  formatted_price: string;
  active: boolean;
}

interface PlanOption {
  priceId: string;
  productId: string;
  productName: string;
  description: string;
  formattedPrice: string;
  formattedMonthlyPrice?: string;
  buttonText: string;
  features: string[];
  amount: number;
  currency: string;
}

interface CurrentPlan {
  productId: string;
  productName: string;
  productDescription: string;
  priceId: string;
  amount: number;
  currency: string;
  interval: string;
  formattedPrice: string;
  metadata: Record<string, string>;
}

interface CurrentSubscription {
  status: string;
  isActive: boolean;
  plan: string;
  startDate: string;
  endDate: string;
  // Organisation name associated with the subscription (if provided by backend)
  organisationName?: string;
  paymentMethod: {
    brand: string;
    last4: string;
    expMonth: number;
    expYear: number;
    funding: string;
  };
  currentPlan: CurrentPlan;
}

interface BillingPageResponse {
  success: boolean;
  hasCustomer: boolean;
  hasSubscription: boolean;
  availableProducts: Product[];
  planOptions: {
    monthly: PriceOption[];
    yearly: PriceOption[];
  };
  customerId?: string;
  // Some responses include organisation name at the root level
  organisationName?: string;
  OrganisationName?: string; // Keep variant for backward compatibility
  currentSubscription?: CurrentSubscription;
  portalUrl?: string;
  invoices?: any[];
}

interface TransformedBillingResponse extends Omit<BillingPageResponse, 'planOptions'> {
  planOptions: {
    monthly: PlanOption[];
    yearly: PlanOption[];
  };
}

// Helper function to transform metadata into features array
const transformMetadataToFeatures = (metadata: {[key: string]: string}, t?: (key: string, fallback?: string) => string): string[] => {
  const features: string[] = [];
  
  // Define the desired order of features
  const featureOrder = [
    'brands',
    'personas', 
    'socials',
    'links / QR Codes',
    'ideas',
    'posts',
    'users'
  ];
  
  // Process features in the specified order
  featureOrder.forEach(key => {
    const value = metadata[key];
    if (value) {
      switch (key) {
        case 'brands':
          if (t) {
            const unlimited = t('billing.features.unlimited', 'Unlimited');
            const singular = t('billing.features.brands', 'brand');
            const plural = t('billing.features.brands_plural', 'brands');
            features.push(`${value === '1000' ? unlimited : value} ${parseInt(value) > 1 ? plural : singular}`);
          } else {
            features.push(`${value === '1000' ? 'Unlimited' : value} brand${parseInt(value) > 1 ? 's' : ''}`);
          }
          break;
        case 'personas':
          if (t) {
            const unlimited = t('billing.features.unlimited', 'Unlimited');
            const singular = t('billing.features.personas', 'persona');
            const plural = t('billing.features.personas_plural', 'personas');
            features.push(`${value === '1000' ? unlimited : value} ${parseInt(value) > 1 ? plural : singular}`);
          } else {
            features.push(`${value === '1000' ? 'Unlimited' : value} persona${parseInt(value) > 1 ? 's' : ''}`);
          }
          break;
        case 'socials':
          if (t) {
            const singular = t('billing.features.socials', 'social platform');
            const plural = t('billing.features.socials_plural', 'social platforms');
            features.push(`${value} ${parseInt(value) > 1 ? plural : singular}`);
          } else {
            features.push(`${value} social platform${parseInt(value) > 1 ? 's' : ''}`);
          }
          break;
        case 'links / QR Codes':
          if (t) {
            const text = t('billing.features.links', 'links / QR codes');
            features.push(`${value} ${text}`);
          } else {
            features.push(`${value} links / QR codes`);
          }
          break;
        case 'ideas':
          if (t) {
            const unlimited = t('billing.features.unlimited', 'Unlimited');
            const text = t('billing.features.ideas', 'content ideas');
            features.push(`${value === 'Unlimited' ? unlimited : value} ${text}`);
          } else {
            features.push(`${value === 'Unlimited' ? 'Unlimited' : value} content ideas`);
          }
          break;
        case 'posts':
          if (t) {
            const unlimited = t('billing.features.unlimited', 'Unlimited');
            const text = t('billing.features.posts', 'posts per month');
            features.push(`${value === 'Unlimited' ? unlimited : value} ${text}`);
          } else {
            features.push(`${value === 'Unlimited' ? 'Unlimited' : value} posts per month`);
          }
          break;
        case 'users':
          if (t) {
            const upTo = t('billing.features.upTo', 'Up to');
            const singular = t('billing.features.users', 'team member');
            const plural = t('billing.features.users_plural', 'team members');
            features.push(`${upTo} ${value} ${parseInt(value) > 1 ? plural : singular}`);
          } else {
            features.push(`Up to ${value} team member${parseInt(value) > 1 ? 's' : ''}`);
          }
          break;
      }
    }
  });
  
  // Add any remaining metadata keys that weren't in the predefined order
  Object.entries(metadata).forEach(([key, value]) => {
    // Skip description keys and already processed keys
    if (key.startsWith('description_') || featureOrder.includes(key)) {
      return;
    }
    
    switch (key) {
      case 'ai-credits':
        features.push(`${value} AI credits per month`);
        break;
      default:
        // For any other metadata keys, just display them as is
        features.push(`${key}: ${value}`);
    }
  });
  
  return features;
};

export const useBillingPage = () => {
  return useQuery<TransformedBillingResponse>({
    queryKey: ['billing-page'],
    staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Cache persists for 30 minutes
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await fetch('https://api.socialness.ai/stripe/billing-page', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          returnUrl: window.location.origin + '/billing'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch billing page data');
      }

      const data: BillingPageResponse = await response.json();
      
      // Transform the data to match our expected structure
      const transformPriceOptions = (priceOptions: PriceOption[]): PlanOption[] => {
        return priceOptions.map(priceOption => {
          // Find the corresponding product
          const product = data.availableProducts.find(p => p.id === priceOption.product);
          
          if (!product) {
            console.warn(`Product not found for price option: ${priceOption.product}`);
            return {
              priceId: priceOption.id,
              productId: priceOption.product,
              productName: 'Unknown Product',
              description: 'Product description not available',
              formattedPrice: priceOption.formatted_price,
              buttonText: 'Subscribe',
              features: [],
              amount: priceOption.unit_amount,
              currency: priceOption.currency
            };
          }

          // Calculate monthly price for yearly plans
          let formattedMonthlyPrice: string | undefined;
          if (priceOption.interval === 'year') {
            const monthlyAmount = Math.round(priceOption.unit_amount / 12);
            formattedMonthlyPrice = new Intl.NumberFormat('fr-FR', {
              style: 'currency',
              currency: priceOption.currency.toUpperCase(),
              minimumFractionDigits: 2
            }).format(monthlyAmount / 100);
          }

          return {
            priceId: priceOption.id,
            productId: product.id,
            productName: product.name,
            description: product.description,
            formattedPrice: priceOption.formatted_price,
            formattedMonthlyPrice,
            buttonText: 'Subscribe',
            features: transformMetadataToFeatures(product.metadata),
            amount: priceOption.unit_amount,
            currency: priceOption.currency
          };
        });
      };

      // Return the complete response with all properties preserved
      return {
        success: data.success,
        hasCustomer: data.hasCustomer,
        hasSubscription: data.hasSubscription,
        availableProducts: data.availableProducts,
        customerId: data.customerId,
        currentSubscription: data.currentSubscription,
        portalUrl: data.portalUrl,
        invoices: data.invoices,
        planOptions: {
          monthly: transformPriceOptions(data.planOptions.monthly),
          yearly: transformPriceOptions(data.planOptions.yearly)
        }
      };
    }
  });
};